import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { useEffect, useMemo, useRef } from 'react';
import { getNewChatShortcutLabel } from '../../flora-aside.helpers';
import type { ChatHistory } from '../../flora.type';
import { FloraHistoryList } from '../flora-history-list/flora-history-list';

interface FloraChatHistoryDropdownProps {
  isOpen: boolean;
  chatHistories: ChatHistory[];
  activeChatId: string | undefined;
  /** True until the first histories payload arrives. */
  isLoading?: boolean;
  onClose: () => void;
  onSetThreadId: (threadId: string) => void;
  onNewChat: () => void;
  onDeleteHistory: (historyId: string) => void;
  onEditHistory: (historyId: string, description: string) => void;
}

/**
 * Chat-history dropdown for the Flora side panel, anchored under the header's
 * title button. Lists a "New chat" row and the date-grouped history (shared
 * with the full-page sidebar). A click outside the card or Escape closes it;
 * picking a chat or starting a new one also closes it. Focus moves to the
 * "New chat" row on open and returns to the opener on close.
 *
 * Must render inside a `position: relative` container that covers the area
 * below the header — the backdrop and card are absolutely positioned in it.
 *
 * @param {boolean} isOpen - Whether the dropdown is shown
 * @param {ChatHistory[]} chatHistories - Histories to list
 * @param {string | undefined} activeChatId - The open chat, marked with a check
 * @param {boolean} [isLoading] - Shows a spinner while the first histories payload loads
 * @param {() => void} onClose - Closes the dropdown
 * @param {(threadId: string) => void} onSetThreadId - Opens a chat
 * @param {() => void} onNewChat - Starts a new chat
 * @param {(historyId: string) => void} onDeleteHistory - Deletes a chat
 * @param {(historyId: string, description: string) => void} onEditHistory - Renames a chat
 * @returns {JSX.Element | null} The dropdown, or null when closed
 */
export function FloraChatHistoryDropdown({
  isOpen,
  chatHistories,
  activeChatId,
  isLoading = false,
  onClose,
  onSetThreadId,
  onNewChat,
  onDeleteHistory,
  onEditHistory,
}: FloraChatHistoryDropdownProps) {
  const shortcutLabel = useMemo(() => getNewChatShortcutLabel(navigator.platform), []);

  const cardRef = useRef<HTMLDivElement>(null);
  const newChatRef = useRef<HTMLButtonElement>(null);

  // Move focus into the dropdown so keyboard users land on its first action,
  // and hand it back to whatever opened it (the header's title button) when
  // the dropdown closes — by Escape, the backdrop or a selection — rather than
  // leaving it on <body> once the focused row unmounts.
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    newChatRef.current?.focus();
    return () => {
      const focused = document.activeElement;
      if (opener?.isConnected && (!focused || focused === document.body)) opener.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Escape inside a row's ⋯ menu or rename input dismisses just that
      // (Mantine doesn't stop the event), not the whole dropdown.
      const isRowMenuOpen = !!cardRef.current?.querySelector('.row-actions[aria-expanded="true"]');
      const isRenaming = event.target instanceof HTMLInputElement && !!cardRef.current?.contains(event.target);
      if (isRowMenuOpen || isRenaming) return;
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <Backdrop data-testid="flora-history-backdrop" onClick={onClose} />
      <Card ref={cardRef} role="dialog" aria-label="Chat history">
        <NewChatSection>
          <NewChatRow
            ref={newChatRef}
            type="button"
            onClick={() => {
              onNewChat();
              onClose();
            }}
          >
            <IconifyIcon icon="material-symbols:edit-square-outline" width={18} height={18} />
            <NewChatLabel>New chat</NewChatLabel>
            <ShortcutHint>{shortcutLabel}</ShortcutHint>
          </NewChatRow>
        </NewChatSection>
        <Divider />
        <ListScroll>
          <FloraHistoryList
            chatHistories={chatHistories}
            activeChatId={activeChatId}
            showActiveCheck
            menuWithinPortal
            isLoading={isLoading}
            onSelect={(id) => {
              onSetThreadId(id);
              onClose();
            }}
            onDelete={onDeleteHistory}
            onEdit={onEditHistory}
          />
        </ListScroll>
      </Card>
    </>
  );
}

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const Card = styled.div`
  position: absolute;
  top: -2px;
  left: 8px;
  right: 8px;
  max-height: min(560px, calc(100% - 8px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 12px;
  box-shadow: var(--box-shadow-big-soft);
  z-index: 2;
`;

const NewChatSection = styled.div`
  padding: 6px;
  flex-shrink: 0;
`;

const NewChatRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--ink-dark);
  font-family: var(--font-family);
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;

  &:hover,
  &:focus-visible {
    background: var(--sky-lighter);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: -2px;
  }
`;

const NewChatLabel = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
`;

const ShortcutHint = styled.span`
  font-size: 12px;
  color: var(--ink-lighter);
`;

const Divider = styled.div`
  height: 1px;
  flex-shrink: 0;
  background: var(--sky-light);
`;

const ListScroll = styled.div`
  overflow-y: auto;
  padding: 0 6px 8px;
`;
